import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/pages/home/context/LangContext";

const plans = [
  {
    nameEn: "Starter",
    nameAr: "المبتدئ",
    descEn: "Perfect for small hotels and rental businesses just getting started.",
    descAr: "مثالي للفنادق الصغيرة وشركات التأجير التي بدأت للتو.",
    monthlyPrice: 29,
    annualPrice: 23,
    popular: false,
    icon: "ri-seedling-line",
    color: "text-green-600",
    bg: "bg-green-50",
    features: [
      { en: "Up to 20 rooms / 10 vehicles", ar: "حتى 20 غرفة / 10 مركبات" },
      { en: "2 operator accounts", ar: "حسابان للمشغلين" },
      { en: "Guest check-in & check-out", ar: "تسجيل وصول ومغادرة الضيوف" },
      { en: "Basic Rasad compliance", ar: "امتثال رصد الأساسي" },
      { en: "Email support", ar: "دعم عبر البريد الإلكتروني" },
      { en: "Monthly reports", ar: "تقارير شهرية" },
    ],
    notIncluded: [
      { en: "Document scanner (coming soon)", ar: "ماسح الوثائق (قريباً)" },
      { en: "Multi-branch support", ar: "دعم متعدد الفروع" },
    ],
  },
  {
    nameEn: "Professional",
    nameAr: "الاحترافي",
    descEn: "For growing mid-size properties that need advanced reporting and automation.",
    descAr: "للعقارات المتوسطة المتنامية التي تحتاج إلى تقارير متقدمة وأتمتة.",
    monthlyPrice: 79,
    annualPrice: 63,
    popular: true,
    icon: "ri-building-line",
    color: "text-gold",
    bg: "bg-gold/10",
    features: [
      { en: "Up to 100 rooms / 50 vehicles", ar: "حتى 100 غرفة / 50 مركبة" },
      { en: "10 operator accounts", ar: "10 حسابات للمشغلين" },
      { en: "All Starter features", ar: "جميع مميزات المبتدئ" },
      { en: "Advanced analytics & reports", ar: "تحليلات وتقارير متقدمة" },
      { en: "Document scanner (coming soon)", ar: "ماسح الوثائق (قريباً)" },
      { en: "Multi-branch support", ar: "دعم متعدد الفروع" },
      { en: "Priority support", ar: "دعم ذو أولوية" },
      { en: "API access", ar: "الوصول إلى API" },
    ],
    notIncluded: [],
  },
  {
    nameEn: "Enterprise",
    nameAr: "المؤسسي",
    descEn: "For large hotel chains and rental fleets needing custom integration and dedicated support.",
    descAr: "لسلاسل الفنادق الكبيرة وأساطيل التأجير التي تحتاج إلى تكامل مخصص ودعم مخصص.",
    monthlyPrice: 199,
    annualPrice: 159,
    popular: false,
    icon: "ri-building-4-line",
    color: "text-[#92400E]",
    bg: "bg-[#FEF3C7]",
    features: [
      { en: "Unlimited rooms & vehicles", ar: "غرف ومركبات غير محدودة" },
      { en: "Unlimited operators", ar: "مشغلون غير محدودون" },
      { en: "All Professional features", ar: "جميع مميزات الاحترافي" },
      { en: "Custom Rasad integration", ar: "تكامل رصد مخصص" },
      { en: "Dedicated account manager", ar: "مدير حساب مخصص" },
      { en: "SLA guarantee (99.9%)", ar: "ضمان SLA (99.9%)" },
      { en: "Custom branding", ar: "علامة تجارية مخصصة" },
      { en: "On-site training", ar: "تدريب في الموقع" },
    ],
    notIncluded: [],
  },
];

const faqs = [
  {
    en: { q: "Is there a free trial?", a: "Yes — 14 days free, no credit card required. Full access to all Professional features." },
    ar: { q: "هل هناك نسخة تجريبية مجانية؟", a: "نعم — 14 يوماً مجاناً، بدون بطاقة ائتمان. وصول كامل لجميع مميزات الاحترافي." },
  },
  {
    en: { q: "Can I switch plans later?", a: "Absolutely. Upgrade or downgrade at any time. Changes take effect on your next billing cycle." },
    ar: { q: "هل يمكنني تغيير الخطة لاحقاً؟", a: "بالتأكيد. قم بالترقية أو التخفيض في أي وقت. تسري التغييرات في دورة الفوترة التالية." },
  },
  {
    en: { q: "Does it work with our existing scanner hardware?", a: "Not yet — support for USB passport and ID scanners (with MRZ reading) is on our roadmap. Today, a guided form captures every document field Rasad needs, so no special hardware is required." },
    ar: { q: "هل يعمل مع أجهزة الماسح الضوئي الموجودة لدينا؟", a: "ليس بعد — دعم ماسحات الجواز والهوية عبر USB (مع قراءة MRZ) ضمن خطتنا القادمة. حالياً، يجمع نموذج موجَّه كل بيانات الوثيقة التي يحتاجها رصد، فلا حاجة لأجهزة خاصة." },
  },
  {
    en: { q: "Is Rasad integration included in all plans?", a: "Basic Rasad compliance is included in Starter. Full automated submission and compliance dashboard are in Professional and above." },
    ar: { q: "هل تكامل رصد مشمول في جميع الخطط؟", a: "امتثال رصد الأساسي مشمول في المبتدئ. الإرسال التلقائي الكامل ولوحة الامتثال في الاحترافي وما فوق." },
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();
  const { isAr, t } = useLang();

  return (
    <section id="pricing" dir={isAr ? "rtl" : "ltr"} className="py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-4">
            <i className="ri-price-tag-3-line text-gold text-sm"></i>
            <span className={`text-[13px] font-semibold text-[#B8892A] ${isAr ? "font-tajawal" : ""}`}>
              {t("Pricing Plans", "خطط الأسعار")}
            </span>
          </div>
          <h2 className={`font-bold text-3xl md:text-4xl text-ink mb-4 ${isAr ? "font-tajawal" : "font-inter"}`}>
            {t("Transparent Pricing, No Surprises", "أسعار شفافة، بلا مفاجآت")}
          </h2>
          <p className={`text-base max-w-xl mx-auto mb-8 text-muted ${isAr ? "font-tajawal" : ""}`}>
            {t("Start free for 14 days. No credit card required.", "ابدأ مجاناً لمدة 14 يوماً. لا حاجة لبطاقة ائتمان.")}
          </p>

          {/* Toggle */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-line border border-line-strong">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${isAr ? "font-tajawal" : ""} ${
                !annual ? "bg-ink text-white" : "text-muted hover:text-ink"
              }`}
            >
              {t("Monthly", "شهري")}
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${isAr ? "font-tajawal flex-row-reverse" : ""} ${
                annual ? "bg-ink text-white" : "text-muted hover:text-ink"
              }`}
            >
              {t("Annual", "سنوي")}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-gold text-ink">-20%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`relative rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 ${
                plan.popular
                  ? "bg-ink border-2 border-gold"
                  : "bg-white border border-line"
              }`}
            >
              {plan.popular && (
                <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-bold text-ink bg-gold whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}>
                  {t("⭐ Most Popular", "⭐ الأكثر شيوعاً")}
                </div>
              )}

              {/* Plan icon + name */}
              <div className={`flex items-center gap-3 mb-5 ${isAr ? "flex-row-reverse" : ""}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${plan.popular ? "bg-gold/20" : plan.bg}`}>
                  <i className={`${plan.icon} text-lg ${plan.popular ? "text-gold" : plan.color}`}></i>
                </div>
                <div className={isAr ? "text-right" : ""}>
                  <h3 className={`font-bold text-lg ${isAr ? "font-tajawal" : "font-inter"} ${plan.popular ? "text-white" : "text-ink"}`}>
                    {isAr ? plan.nameAr : plan.nameEn}
                  </h3>
                  <p className={`text-xs leading-relaxed ${isAr ? "font-tajawal" : ""} ${plan.popular ? "text-white/50" : "text-muted"}`}>
                    {isAr ? plan.descAr : plan.descEn}
                  </p>
                </div>
              </div>

              {/* Price */}
              <div className={`mb-6 ${isAr ? "text-right" : "text-left"}`}>
                <div className={`flex items-end gap-1 ${isAr ? "flex-row-reverse justify-end" : ""}`}>
                  <span className={`font-inter font-bold text-4xl ${plan.popular ? "text-gold" : "text-ink"}`}>
                    {annual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <div className="pb-1">
                    <p className={`text-sm font-medium ${plan.popular ? "text-white/70" : "text-muted"}`}>OMR</p>
                    <p className={`text-xs ${isAr ? "font-tajawal" : ""} ${plan.popular ? "text-white/40" : "text-muted"}`}>
                      {t("/month", "/شهر")}
                    </p>
                  </div>
                </div>
                {annual && (
                  <p className={`text-xs mt-1 ${isAr ? "font-tajawal" : ""} ${plan.popular ? "text-gold/70" : "text-muted"}`}>
                    {t(`Billed ${plan.annualPrice * 12} OMR/year`, `يُفوتر ${plan.annualPrice * 12} ر.ع/سنة`)}
                  </p>
                )}
              </div>

              <button
                onClick={() => navigate("/register")}
                className={`w-full py-3 rounded-xl font-semibold text-sm mb-6 transition-all cursor-pointer hover:opacity-90 whitespace-nowrap ${isAr ? "font-tajawal" : ""} ${
                  plan.popular ? "bg-gold text-ink" : "bg-ink text-white"
                }`}
              >
                {t("Start Free Trial", "ابدأ مجاناً")}
              </button>

              {/* Features */}
              <ul className="space-y-2.5">
                {plan.features.map((feat, j) => (
                  <li key={j} className={`flex items-center gap-2.5 text-sm ${isAr ? "flex-row-reverse" : ""}`}>
                    <i className="ri-check-line flex-shrink-0 text-gold"></i>
                    <span className={`${isAr ? "font-tajawal" : ""} ${plan.popular ? "text-white/80" : "text-ink-soft"}`}>
                      {isAr ? feat.ar : feat.en}
                    </span>
                  </li>
                ))}
                {plan.notIncluded.map((feat, j) => (
                  <li key={`ni-${j}`} className={`flex items-center gap-2.5 text-sm opacity-40 ${isAr ? "flex-row-reverse" : ""}`}>
                    <i className="ri-close-line flex-shrink-0 text-muted"></i>
                    <span className={`${isAr ? "font-tajawal" : ""} ${plan.popular ? "text-white/50" : "text-muted"}`}>
                      {isAr ? feat.ar : feat.en}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h3 className={`font-bold text-xl text-ink mb-6 text-center ${isAr ? "font-tajawal" : "font-inter"}`}>
            {t("Frequently Asked Questions", "الأسئلة الشائعة")}
          </h3>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-line overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className={`w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-cream transition-colors ${isAr ? "flex-row-reverse" : ""}`}
                >
                  <span className={`text-sm font-semibold text-ink ${isAr ? "font-tajawal text-right" : "text-left"}`}>
                    {isAr ? faq.ar.q : faq.en.q}
                  </span>
                  <i className={`text-gold transition-transform flex-shrink-0 ${openFaq === i ? "ri-subtract-line" : "ri-add-line"}`}></i>
                </button>
                {openFaq === i && (
                  <div className={`px-5 pb-4 text-sm text-muted leading-relaxed ${isAr ? "font-tajawal text-right" : ""}`}>
                    {isAr ? faq.ar.a : faq.en.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <p className={`text-center mt-8 text-sm text-muted ${isAr ? "font-tajawal" : ""}`}>
          {t("Need a custom solution?", "تحتاج إلى حل مخصص؟")}{" "}
          <button
            onClick={() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" })}
            className="font-semibold text-gold cursor-pointer hover:underline"
          >
            {t("Contact our sales team", "تواصل مع فريق المبيعات")}
          </button>
        </p>
      </div>
    </section>
  );
}
